// Title / description templates for scheduled games.
//
// A template is plain text with <token> placeholders. Tokens resolve from the
// game row + its season; unknown tokens are left as typed so a typo is visible
// in the preview rather than silently blank. Matching is case-insensitive
// (<Class-name> and <class-name> are the same token).

export const TOKENS = [
  { key: 'away-team',    label: 'Away team',      hint: 'Morgantown' },
  { key: 'home-team',    label: 'Home team',      hint: 'Deer Lakes' },
  { key: 'venue',        label: 'Venue',          hint: 'Alpha Ice Complex' },
  { key: 'date',         label: 'Date',           hint: 'Oct 1' },
  { key: 'date-long',    label: 'Date (long)',    hint: 'Thursday, October 1, 2026' },
  { key: 'date-numeric', label: 'Date (numeric)', hint: '10/1/2026' },
  { key: 'weekday',      label: 'Weekday',        hint: 'Thursday' },
  { key: 'month',        label: 'Month',          hint: 'October' },
  { key: 'day',          label: 'Day',            hint: '1' },
  { key: 'year',         label: 'Year',           hint: '2026' },
  { key: 'time',         label: 'Time',           hint: '8:00 PM' },
  { key: 'time-tz',      label: 'Time + zone',    hint: '8:00 PM EDT' },
  { key: 'class',        label: 'Class',          hint: 'AAA' },
  { key: 'class-name',   label: 'Class (full)',   hint: 'Class AAA / A Gold' },
  { key: 'league',       label: 'League',         hint: 'PIHL' },
  { key: 'sport',        label: 'Sport',          hint: 'hockey' },
  { key: 'season',       label: 'Season label',   hint: 'Varsity 2026-27' },
  { key: 'season-years', label: 'Season years',   hint: '2026/27' },
  { key: 'week',         label: 'Game week',      hint: '3' },
  { key: 'game-number',  label: 'Game #',         hint: '682' },
  { key: 'away-score',   label: 'Away score',     hint: '3' },
  { key: 'home-score',   label: 'Home score',     hint: '2' },
]

const localDate = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d) }

// "2026-09-28".."2027-02-26" → "2026/27"; a season inside one calendar year → "2026".
export function seasonYears(firstDate, lastDate) {
  if (!firstDate) return ''
  const a = firstDate.slice(0, 4)
  const b = (lastDate || firstDate).slice(0, 4)
  return a === b ? a : `${a}/${b.slice(2)}`
}

// Build the token → value map for one game.
export function gameContext(g, season, { week = '', years = '' } = {}) {
  const d = localDate(g.game_date)
  const time = (g.game_time || '').replace(/\s*(EDT|EST|ET|CDT|CST)\s*$/i, '')
  const cls = g.division_group || ''
  const clsName = g.division || ''
  return {
    'away-team': g.visitor || '',
    'home-team': g.home || '',
    'venue': g.location || '',
    'date': d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    'date-long': d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    'date-numeric': `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`,
    'weekday': d.toLocaleDateString('en-US', { weekday: 'long' }),
    'month': d.toLocaleDateString('en-US', { month: 'long' }),
    'day': String(d.getDate()),
    'year': String(d.getFullYear()),
    'time': time || 'TBD',
    'time-tz': g.game_time || 'TBD',
    'class': cls,
    'class-name': clsName,
    'league': season?.league || '',
    'sport': season?.sport || '',
    'season': season?.label || '',
    'season-years': years,
    'week': week === '' ? '' : String(week),
    'game-number': g.game_number || '',
    'away-score': g.visitor_score ?? '',
    'home-score': g.home_score ?? '',
  }
}

export function renderTemplate(template, ctx) {
  return String(template || '').replace(/<([a-z][a-z0-9-]*)>/gi, (m, key) => {
    const k = key.toLowerCase()
    return Object.prototype.hasOwnProperty.call(ctx, k) ? String(ctx[k]) : m
  }).replace(/\s{2,}/g, ' ').trim()
}

// Tokens used in a template that don't exist — surfaced by the builder.
export function unknownTokens(template) {
  const known = new Set(TOKENS.map(t => t.key))
  const out = new Set()
  for (const m of String(template || '').matchAll(/<([a-z][a-z0-9-]*)>/gi)) {
    if (!known.has(m[1].toLowerCase())) out.add(m[1])
  }
  return [...out]
}
